import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCategories } from "../api/swr/useCategories";
import { PlatformRole } from "~/config/types";
import { Button } from "~/atoms/button";
import { createUser, updateUser } from "~/api/v2/requests";
import { type User } from "~/api/types";
import { useUserContext } from "~/hooks/useUserContext";
import { RadioGroup, RadioGroupItem } from "~/atoms/radio-group";
import { Label } from "~/atoms/label";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import CheckboxWithLabel from "~/molecules/CheckboxWithLabel";

interface Props {
  isOpen: boolean;
  userData?: User;
  onSubmit: () => void;
  onClose: () => void;
}

const roles = [
  PlatformRole.superAdmin,
  PlatformRole.coordinator,
  PlatformRole.contributor,
  PlatformRole.reader,
] as const;

const UserFormSchema = z.object({
  email: z.email({ message: i18n.t(($) => $.validations.email) }),
  firstName: z.string({ message: i18n.t(($) => $.validations.required) }).min(
    1,
    i18n.t(($) => $.validations.required),
  ),
  lastName: z.string({ message: i18n.t(($) => $.validations.required) }).min(
    1,
    i18n.t(($) => $.validations.required),
  ),
  categoryIds: z.array(z.number()).min(
    1,
    i18n.t(($) => $.validations.required),
  ),
  position: z.string().optional(),
  role: z.enum(PlatformRole),
});

type UserFormValues = z.infer<typeof UserFormSchema>;

export const UserModal = ({ isOpen, userData, onSubmit, onClose }: Props) => {
  const { t } = useTranslation();

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {userData
              ? t(($) => $.settings.userManagement.editUser.title)
              : t(($) => $.settings.userManagement.addUser.title)}
          </DialogTitle>
        </DialogHeader>
        <UserForm userData={userData} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
};

interface FormProps {
  userData?: User;
  onSubmit: () => void;
}

const UserForm = ({ userData, onSubmit }: FormProps) => {
  const { t } = useTranslation();

  const { user, setUser } = useUserContext();
  const { data: categories } = useCategories();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(UserFormSchema),
    values: {
      email: userData?.email ?? "",
      firstName: userData?.firstName ?? "",
      lastName: userData?.lastName ?? "",
      position: userData?.position ?? "",
      categoryIds: userData?.categories?.map((d) => d.id) ?? [],
      role: userData?.role ?? PlatformRole.reader,
    },
  });

  const categoryOptions = categories?.map((d) => ({
    label: d.title,
    value: d.id,
  }));

  const handleSubmit = async (values: UserFormValues) => {
    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      role: values.role,
      position: values.position,
      categoryIds: values.categoryIds,
    };

    try {
      setIsLoading(true);
      if (!userData) {
        await createUser(data);
      } else {
        const updatedUser = await updateUser(userData.id, data);
        if (userData?.id === user.id) setUser({ ...user, ...updatedUser });
      }
      onSubmit();
    } catch {
      toast.error(
        userData
          ? t(($) => $.toasts.error.editUser)
          : t(($) => $.toasts.error.addUser),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.settings.profile.email)}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.settings.profile.firstName)}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.settings.profile.lastName)}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.commons.role)}</FormLabel>
              <FormControl>
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {roles.map((role) => (
                      <Fragment key={role}>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem
                            value={role}
                            id={role}
                            disabled={
                              (role === PlatformRole.superAdmin &&
                                user?.role !== PlatformRole.superAdmin) ||
                              (role === PlatformRole.coordinator &&
                                user?.role !== PlatformRole.superAdmin &&
                                user?.role !== PlatformRole.coordinator)
                            }
                          />
                          <Label htmlFor={role}>
                            {t(($) => $.commons.roles[role])}
                          </Label>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          <p>{t(($) => $.settings.roleDescription[role])}</p>
                        </div>
                      </Fragment>
                    ))}
                  </RadioGroup>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="categoryIds"
          render={() => (
            <FormItem>
              <FormLabel>{t(($) => $.commons.document.category)}</FormLabel>
              <div className="grid grid-cols-2 gap-2">
                {categoryOptions?.map(({ label, value }) => (
                  <FormField
                    key={value}
                    control={form.control}
                    name="categoryIds"
                    render={({ field }) => {
                      const categoryIds = field.value ?? [];
                      return (
                        <FormItem key={value}>
                          <FormControl>
                            <CheckboxWithLabel
                              id={value.toString()}
                              label={label}
                              checked={categoryIds.includes(value)}
                              onCheckedChange={(checked) =>
                                field.onChange(
                                  checked
                                    ? [...categoryIds, value]
                                    : categoryIds.filter((v) => v !== value),
                                )
                              }
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.settings.profile.position)}</FormLabel>
              <FormControl>
                <Input {...field} value={field.value} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={isLoading}>
            {t(($) => $.commons.buttons.confirm)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
